import { Component, OnInit, Input } from '@angular/core';
import { Router } from '@angular/router';
import { StatementGeneratorService } from '../../services/statement-generator.service';
import { StatementGenerationListItem } from '../../models/statement.models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-statement-generations-list',
  templateUrl: './statement-generations-list.component.html',
  styleUrls: ['./statement-generations-list.component.scss']
})
export class StatementGenerationsListComponent implements OnInit {

  @Input() projectId: number;
  @Input() projectData: any;

  generations: StatementGenerationListItem[] = [];
  filteredGenerations: StatementGenerationListItem[] = [];

  loading = false;
  searchTerm = '';
  statusFilter = 'all';
  sortOrder = 'newest';

  constructor(
    private router: Router,
    private statementService: StatementGeneratorService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.loadGenerations();
  }

  /**
   * Load all generations for the project
   */
  loadGenerations(): void {
    if (!this.projectId) {
      console.warn('No projectId provided to loadGenerations');
      return;
    }

    console.log('Loading generations for projectId:', this.projectId);
    this.loading = true;

    this.statementService.getProjectGenerations(
      this.projectId,
      (generations: any[]) => {
        console.log('Generations loaded:', generations);
        this.generations = generations;
        this.applyFilters();
        this.loading = false;
      },
      (error) => {
        console.error('Error loading generations:', error);
        console.error('Error status:', error?.status);
        console.error('Error message:', error?.message);

        this.loading = false;

        // Show more detailed error message
        if (error?.status === 404) {
          this.showMessage('Backend endpoint not found: /api/projects/' + this.projectId + '/statement-generations', 'error');
          console.error('The backend needs to implement GET /api/projects/{projectId}/statement-generations endpoint');
        } else {
          this.showMessage('Error loading statement generations: ' + (error?.message || 'Unknown error'), 'error');
        }
      }
    );
  }

  /**
   * Apply search and filters
   */
  applyFilters(): void {
    let filtered = [...this.generations];

    // Search filter
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(g =>
        g.name?.toLowerCase().includes(term) ||
        g.message?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (this.statusFilter !== 'all') {
      filtered = filtered.filter(g => g.status === this.statusFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return this.sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    this.filteredGenerations = filtered;
  }

  /**
   * Navigate to generation details
   */
  viewGeneration(generation: StatementGenerationListItem): void {
    this.router.navigate(['/projects', this.projectId, 'statement-generations', generation.id]);
  }

  /**
   * Get status badge class
   */
  getStatusBadgeClass(status: string): string {
    const classes = {
      'completed': 'badge-success',
      'processing': 'badge-warning',
      'failed': 'badge-danger',
      'pending': 'badge-secondary',
      'needs_input': 'badge-info'  // Informational style (blue) for needs_input
    };
    return classes[status] || 'badge-secondary';
  }

  /**
   * Get status icon
   */
  getStatusIcon(status: string): string {
    const icons = {
      'completed': 'check_circle',
      'processing': 'hourglass_empty',
      'failed': 'error',
      'pending': 'schedule',
      'needs_input': 'info'  // Info icon for needs_input
    };
    return icons[status] || 'help';
  }

  /**
   * Get status label
   */
  getStatusLabel(status: string): string {
    const labels = {
      'completed': 'Completato',
      'processing': 'In elaborazione',
      'failed': 'Fallito',
      'pending': 'In attesa',
      'needs_input': 'Richiede dati aggiuntivi'  // Italian: "Requires additional data"
    };
    return labels[status] || status;
  }

  /**
   * Refresh status for a generation
   */
  refreshStatus(generation: StatementGenerationListItem, event: Event): void {
    event.stopPropagation();

    this.statementService.refreshGenerationStatus(
      generation.id,
      (status) => {
        const index = this.generations.findIndex(g => g.id === generation.id);
        if (index !== -1) {
          this.generations[index] = { ...this.generations[index], ...status };
          this.applyFilters();
        }
      },
      (error) => {
        console.error('Error refreshing status:', error);
      }
    );
  }

  /**
   * Delete a generation and all its statements
   */
  deleteGeneration(generation: StatementGenerationListItem, event: Event): void {
    event.stopPropagation();

    const confirmMessage = `Sei sicuro di voler eliminare questa generazione?\n\n"${generation.name}"\n\nQuesta azione eliminerà tutti gli statement associati e non può essere annullata.`;

    if (!confirm(confirmMessage)) {
      return;
    }

    console.log(`🗑️ Deleting generation: ${generation.id}`);

    this.statementService.deleteStatementGeneration(
      generation.id,
      (result) => {
        console.log('✅ Generation deleted successfully');
        // Remove from local array
        this.generations = this.generations.filter(g => g.id !== generation.id);
        this.applyFilters();
        this.showMessage('Generazione eliminata con successo', 'success');
      },
      (error) => {
        console.error('❌ Error deleting generation:', error);
        this.showMessage('Errore durante l\'eliminazione della generazione', 'error');
      }
    );
  }

  /**
   * Show message
   */
  private showMessage(message: string, type: 'success' | 'error' | 'warning'): void {
    this.snackBar.open(message, '', {
      duration: 3000,
      panelClass: [`snackbar-${type}`],
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }
}
